import * as React from 'react'
import { StoreProps } from '../../../services/stores/casting.store'
import { I18nService } from '../../../services/utils/i18n.service'
import { InfoBoxError } from './casting-styled.component'
import CastingAttachmentsDropzoneComponent from './attachments/casting-attachments-dropzone.component'
import CastingBackofficeCustomerPickerComponent from './backoffice-customer/casting-backoffice-customer-picker.component'
import CastingCityInputComponent from './city/casting-city-input.component'
import CastingCityResultsPickerComponent from './city/casting-city-results-picker.component'
import CastingCombinationAttributesPickerComponent from './combination-attributes/casting-combination-attributes-picker.component'
import CastingCouponPickerComponent from './coupon/casting-coupon-picker.component'
import CastingDurationCalculatorLegacyComponent from './duration/casting-duration-calculator-legacy.component'
import CastingEndDatePickerComponent from './end-date/casting-end-date-picker.component'
import CastingMessageComponent from './finalize/casting-message.component'
import CastingFrequencyContentComponent from './frequency/casting-frequency-content.component'
import CastingOfferedServiceTreeComponent from './offered-service/casting-offered-service-tree.component'
import CastingOptionsPickerComponent from './options/casting-options-picker.component'
import CastingPreferencesPickerComponent from './preferences/casting-preferences-picker.component'
import CastingQuantityPickerComponent from './quantity/casting-quantity-picker.component'
import CastingRequirePlaceInterventionPlaceComponent from './required-place/casting-required-place-intervention-place.component'
import CastingRequiredPlaceServiceDeliveryLocationComponent from './required-place/casting-required-place-service-delivery-location.component'
import CastingStartDatePickerComponent from './start-date/casting-start-date-picker.component'
import CastingTasksPickerComponent from './tasks/casting-tasks-picker.component'
import { CastingBackofficeServiceDeliveryFormComponent } from './backoffice-service-delivery/casting-backoffice-service-delivery-form.component'
import LoadingComponent from '../../shared/loading.component'
import CastingSuspendWhileRealtimeLoadingComponent from './casting-suspend-while-realtime-loading.component'
import CastingComplexContentComponent from './complex/casting-complex-content.component'
import ComplexVariantFormComponent from './complex-variant/complex-variant-form.component'
import BackofficeCustomPriceFormComponent from './backoffice-custom-price/backoffice-custom-price-form.component'
import CastingEmployeeQuotationFormComponent from './employee-quotation/casting-employee-quotation-form.component'
import CastingLodgingContentComponent from './lodging/casting-lodging-content.component'
import CastingEnableLodgingContentComponent from './enable-lodging/casting-enable-lodging-content.component'
import LodgingIcalContentComponent from './lodging-ical/lodging-ical-content.component'
import LodgingCheckInOutComponent from './lodging-check-in-out/lodging-check-in-out.component'
import CastingDurationCalculatorModernComponent from './duration/casting-duration-calculator-modern.component'
import CastingQuantityDiscountPickerComponent from './quantity-discounts/casting-quantity-discount-picker.component'
import CastingTooltipButtonComponent from './casting-tooltip-button.component'
import CastingBackofficeAppointmentFormComponent from './backoffice-appointment/casting-backoffice-appointment-form.component'
import CastingParticipantsNumberInputComponent from './participants/casting-participants-number-input.component'
import GroupSessionPickerComponent from './group-session/group-session-picker.component'
import CastingCustomerPlanPickerComponent from './customer-plan/casting-customer-plan-picker.component'

const CastingPriceDetailsComponent = React.lazy(() => import('./price/casting-price-details.component'))
const i18nScoped = new I18nService(require('./casting-tooltip-content.component.yml'))

export default function CastingTooltipContentComponent (props: StoreProps & { focusableInput?: StoreProps['data']['focusedInput'] }) {
  const { store, store: { data, data: { renderingMode, serviceCategory, serviceCategories, city, cityResults, offeredService, requiredPlace, slots, offeredServiceEnableSearch } } } = props
  const focusableInput = props.focusableInput || props.data.focusedInput

  React.useEffect(() => {
    if (!slots.length && (focusableInput === 'frequency' || focusableInput === 'complex' || focusableInput === 'start-date' || focusableInput === 'group-session')) {
      store.initSlotWithDuration(store.getMinDuration())
    }
  }, [slots.length, focusableInput, store])

  const renderButtonNext = React.useCallback((opts: { disabled?: boolean } = {}) => {
    const disabled = 'disabled' in opts ? opts.disabled : store.getProgressForStep(focusableInput) !== 100

    return <CastingTooltipButtonComponent
      id="js-test-tooltip-next"
      renderingMode={ renderingMode }
      disabled={ disabled }
      children={ i18nScoped.t('next') }
      onClick={ () => store.nextFocus() }/>
  }, [focusableInput, renderingMode, store])

  switch (focusableInput) {
    case 'city':
      return <React.Fragment>
        { renderingMode === 'mobile' && <CastingCityInputComponent store={ store } data={ data } mode="mobile-inside-tooltip" /> }

        <div className={ `stepTooltip-postalCode ${cityResults && cityResults.status === 'loading' ? 'stepTooltip-postalCode--loading' : ''}` }>
          { <CastingCityResultsPickerComponent store={ store } data={ data } /> }
        </div>
      </React.Fragment>

    case 'offered-service': {
      if (!city) return <InfoBoxError>{ i18nScoped.t('no_city') }</InfoBoxError>
      const searchIcon = <a
        className="icon-search"
        style={{ float: 'right' }}
        title={ i18nScoped.t(offeredServiceEnableSearch ? 'disable_search_with_keyword' : 'enable_search_with_keywords') }
        onClick={ () => store.set({ offeredServiceEnableSearch: !offeredServiceEnableSearch }) } />

      if (serviceCategory) {
        const parentCategory = serviceCategories.find(_ => _.id === serviceCategory.parent_category_id)

        var title = <h3 className="stepTooltip-title txtS u-mbm">
          <a id="js-test-go-upper-category" className="icon-prev buttonNeutral button--XS u-mrm" onClick={ () => store.set({ serviceCategory: parentCategory }) }></a>
          { serviceCategory.name }
          { searchIcon }
        </h3>
      } else {
        title = <h3 className="stepTooltip-title txtS u-mbm">{ i18nScoped.t('our_services') } { searchIcon }</h3>
      }

      return <React.Fragment>
        { title } <CastingOfferedServiceTreeComponent store={ store } data={ data } />
      </React.Fragment>
    }

    case 'enable-lodging':
      return <CastingEnableLodgingContentComponent store={ store } data={ data } />

    case 'lodging':
      return <React.Fragment>
        <CastingLodgingContentComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'lodging-ical':
      return <React.Fragment>
        <LodgingIcalContentComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'lodging-check-in-out':
      return <React.Fragment>
        <LodgingCheckInOutComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'required-place':
      return <React.Fragment>
        <CastingRequirePlaceInterventionPlaceComponent store={ store } data={ data } itemByLine={ 1 } />
        { requiredPlace === 'on_site' && <CastingRequiredPlaceServiceDeliveryLocationComponent store={ store } data={ data } /> }
      </React.Fragment>

    case 'combination-attributes':
    case 'combination-attributes-after-offered-service':
      return <React.Fragment>
        <CastingCombinationAttributesPickerComponent
          store={ store }
          data={ data }
          allowedOfferedServiceAttrs={ focusableInput === 'combination-attributes' ? store.getAllowedAttributesOfStep2() : store.getAllowedAttributeJustAfterOfferedService() } />
        { renderButtonNext() }
      </React.Fragment>

    case 'customer-plan':
      return <CastingCustomerPlanPickerComponent store={ store } data={ data } />

    case 'coupon':
      return <CastingCouponPickerComponent store={ store } data={ data } />

    case 'quantity-discount':
      return <CastingQuantityDiscountPickerComponent store={ store } data={ data } />

    case 'group-session':
      return <React.Fragment>
        <GroupSessionPickerComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'complex-variant':
      return <React.Fragment>
        <ComplexVariantFormComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'duration':
      return <React.Fragment>
        {  offeredService.enable_duration_calculator
          ? <CastingDurationCalculatorModernComponent store={ store } data={ data } />
          : <CastingDurationCalculatorLegacyComponent store={ store } data={ data } /> }
        { renderButtonNext() }
      </React.Fragment>

    case 'quantity':
      if (offeredService) return <CastingQuantityPickerComponent store={ store } data={ data } />
      return <InfoBoxError>{ i18nScoped.t('undefined_service') }</InfoBoxError>

    case 'complex':
      return <React.Fragment>
        { slots.length > 0 && <CastingComplexContentComponent store={ store } data={ data } /> }
        { renderButtonNext() }
      </React.Fragment>

    case 'frequency':
      return <React.Fragment>
        { slots.length > 0 && <CastingFrequencyContentComponent store={ store } data={ data } /> }
        { renderButtonNext() }
      </React.Fragment>

    case 'start-date':
      if (!slots.length) return null

      return <CastingSuspendWhileRealtimeLoadingComponent store={ store } data={ data }>
        <CastingStartDatePickerComponent store={ store } data={ data } />
        { renderButtonNext() }
      </CastingSuspendWhileRealtimeLoadingComponent>

    case 'end-date':
      if (!slots.length) return null
      return <React.Fragment>
        <CastingEndDatePickerComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'options':
      if (!offeredService) return <InfoBoxError>{ i18nScoped.t('undefined_service') }</InfoBoxError>

      return <React.Fragment>
        <CastingOptionsPickerComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'preferences':
      return <React.Fragment>
        <CastingPreferencesPickerComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'tasks':
      return <React.Fragment>
        <CastingTasksPickerComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'message':
      return <React.Fragment>
        <CastingMessageComponent store={ store } data={ data } rows={ 10 } />
        { renderButtonNext() }
      </React.Fragment>

    case 'participants':
      return <>
        <CastingParticipantsNumberInputComponent store={ store } data={ data } />
        { renderButtonNext() }
      </>

    case 'attachments':
      return <React.Fragment>
        <p className="stepTooltip-subTitle u-mbm">{ i18nScoped.t('subtitle.attachments') }</p>
        <CastingAttachmentsDropzoneComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'backoffice-customer':
      return <CastingBackofficeCustomerPickerComponent store={ store } data={ data } />

    case 'backoffice-service-delivery':
      return <React.Fragment>
        <CastingBackofficeServiceDeliveryFormComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'backoffice-custom-price':
      return <React.Fragment>
        <BackofficeCustomPriceFormComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'employee-quotation':
      return <React.Fragment>
        <CastingEmployeeQuotationFormComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'backoffice-appointment':
      return <React.Fragment>
        <CastingBackofficeAppointmentFormComponent store={ store } data={ data } />
        { renderButtonNext() }
      </React.Fragment>

    case 'price':
      return <React.Suspense fallback={ <LoadingComponent /> }>
        <CastingPriceDetailsComponent store={ store } data={ data } />
      </React.Suspense>
      
    case 'end-of-step-1':
    case 'end-of-step-2':
      return null

    case null:
      return null // may happen if relying on this.props.focusedInput, that may be reset on tooltip close.
  }
}
